import {
  defaultWineOrder,
  wineOrderKeys,
  type WineOrder,
  type WineOrderKey,
} from "@corklog/shared";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { narrowed, resting, Select } from "./controls.js";

/**
 * The order the list is read in: what it is sorted by, and which end first.
 *
 * **The direction is a button that says what it is doing, in the key's own
 * words.** *Stigande* and *fallande* are true of every key and mean nothing
 * for most of them — which end of a drinking window is *ascending* — so the
 * button reads *äldst först* for a vintage and *brådskande först* for a
 * window, and pressing it says the other one. It is a button rather than a
 * second select because there are only ever two answers, and the one being
 * asked for is to turn the list round, not to choose from a list.
 *
 * **Brass when it is not the list's own order**, by the rule the narrowings
 * above follow: the colour marks what is doing something. Each of the two
 * controls turns brass for its own half, so a list sorted by vintage oldest
 * first shows which of the two was changed.
 *
 * Two equal columns, capped at the search field's width, rather than a place in
 * the narrowings' grid. There they would push the window narrowing onto a
 * second line on a desktop, and they would read as two more ways of hiding
 * wines, which neither of them is.
 */
export function WineSorting({
  order,
  onChange,
  dated,
}: {
  order: WineOrder;
  onChange: (order: WineOrder) => void;
  /** Whether *Tillagd* is offered, and so which order is the resting one. */
  dated: boolean;
}) {
  const { t } = useTranslation();
  const keyId = useId();
  // Not `resting`, which is the controls' class name for a control at rest.
  const opensIn = defaultWineOrder(dated);

  const keyChanged = order.key !== opensIn.key;
  const turned = order.direction !== opensIn.direction;

  return (
    <div className="mt-4 grid max-w-md grid-cols-2 gap-4">
      <div>
        <label
          className={`block text-sm ${keyChanged ? "text-accent" : "text-muted"}`}
          htmlFor={keyId}
        >
          {t("wines.order.label")}
        </label>
        <div className="mt-1">
          <Select
            id={keyId}
            value={order.key}
            // A new key starts from its own natural end — the oldest vintage,
            // the most urgent window — rather than inheriting a direction that
            // was chosen for a different question.
            onChange={(next) =>
              onChange({
                key: next as WineOrderKey,
                direction: opensIn.direction,
              })
            }
            active={keyChanged}
          >
            {wineOrderKeys(dated).map((key) => (
              <option key={key} value={key}>
                {t(`wines.order.key.${key}`)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div>
        {/* A caption rather than a <label>: a label would become the button's
            name, and the name has to be what the button currently says. */}
        <span
          aria-hidden="true"
          className={`block text-sm ${turned ? "text-accent" : "text-muted"}`}
        >
          {t("wines.order.direction")}
        </span>
        <button
          type="button"
          onClick={() =>
            onChange({ ...order, direction: order.direction === "asc" ? "desc" : "asc" })
          }
          className={`bg-surface hover:bg-raised mt-1 flex h-11 w-full items-center justify-between gap-2 rounded-sm border px-3 text-left text-base transition-colors ${
            turned ? narrowed : resting
          }`}
        >
          <span>{t(`wines.order.${order.key}.${order.direction}`)}</span>
          <Arrow down={order.direction === "desc"} />
        </button>
      </div>
    </div>
  );
}

/**
 * The order in a few words, for the folded line on a phone: *Årgång, äldst
 * först*. The same two strings the controls show, so the line and the controls
 * cannot describe the order differently.
 */
export function describeOrder(order: WineOrder, t: TFunction): string {
  return t("wines.order.summary", {
    key: t(`wines.order.key.${order.key}`),
    direction: t(`wines.order.${order.key}.${order.direction}`).toLocaleLowerCase("sv"),
  });
}

/** Which way the list runs, drawn in the chevron's hand so the two controls
 *  read as one pair. */
function Arrow({ down }: { down: boolean }) {
  return (
    <svg
      viewBox="0 0 10 12"
      aria-hidden="true"
      className={`h-3 w-2.5 shrink-0 transition-transform ${down ? "rotate-180" : ""}`}
    >
      <path
        d="M5 11V1M1 5l4-4 4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
